// Session AEY: the Call-Out Attendance Report workflow (office). One component, two entry points
// (a fresh report from the call-outs list, or a report pre-filled from an existing call-out). It
// resolves the customer (an existing V2 customer, or a one-off caller), records the attendance,
// shows a server-authoritative live charge preview, generates the branded PDF and (optionally) files
// it to Drive, and raises a Xero DRAFT invoice whose number becomes the report reference.
//
// The Xero-contact-import scenario was dropped for v1 (Director-confirmed). Since Session AFM the
// fresh entry points are "Existing customer" (search the register, which includes previous one-off
// callers, shown "(non-contract)") and "Not in the platform?" -- an inline form that CREATES a real
// NON_CONTRACT Customer record (with its site and a Xero contact) and then proceeds as an existing
// customer, so the caller is findable, promotable and cleanly invoiced next time. The record-less
// legacy "oneoff" mode remains ONLY for regenerating a pre-AFM report whose caller lives in the
// CallOut's ad-hoc fields.
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, apiRequest } from "../../lib/api";
import type {
  CalloutReportInput,
  CalloutReportPreviewResult,
  CalloutReportStatus,
  CalloutReportTier,
  CalloutReportResult,
} from "../../lib/api";
import { customers as customersResource } from "../../lib/resources";
import { erasedPersonRefusal } from "../../lib/erasedPerson";
import type { Customer } from "../../lib/resources";
import { RecordPicker, type PickerRecord } from "../../components/RecordPicker";
import { MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { deriveOutOfHours, manualInvoiceRefError } from "../../lib/calloutCharge";
import { parseAgreedPrice } from "./calloutRates";
import { gbpExact } from "../../lib/format";

const inputClass = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelClass = "mb-1 block text-xs font-menu uppercase tracking-wide text-ele-body";

const STATUS_CARDS: { value: CalloutReportStatus; label: string; ring: string; text: string }[] = [
  { value: "service", label: "Left in service", ring: "border-ele-green", text: "text-ele-green" },
  { value: "isolated", label: "Left isolated", ring: "border-ele-amber-line", text: "text-amber-400" },
  { value: "out", label: "Left out of service", ring: "border-ele-danger", text: "text-ele-danger" },
];

const TIERS: { value: CalloutReportTier; label: string }[] = [
  { value: "core", label: "Core" },
  { value: "care", label: "Care" },
  { value: "complete", label: "Complete" },
  { value: "non-contract", label: "Non-contract" },
];

// The editable form. Kept flat; the API body is assembled on generate.
interface FormState {
  calloutId: string | null;
  customerMode: "existing" | "oneoff";
  customerId: string | null;
  siteId: string | null;
  assetId: string | null;
  oneOffCompany: string;
  oneOffContact: string;
  oneOffAddress: string;
  oneOffEmail: string;
  oneOffPhone: string;
  status: CalloutReportStatus;
  siteName: string;
  siteAddress: string;
  liftDesignation: string;
  liftType: string;
  locationInBuilding: string;
  manufacturer: string;
  serialNumber: string;
  controllerDrive: string;
  dateReported: string;
  dateAttended: string;
  reportedBy: string;
  natureOfCallout: string;
  timeOn: string;
  timeOff: string;
  travelHours: number;
  addlHours: number;
  oohOverride: boolean | null; // null = auto-derive from the times
  faultReported: string;
  findingsAndCause: string;
  workCarriedOut: string;
  partsUsed: string;
  partsOnOrder: string;
  furtherWorks: string;
  additionalComments: string;
  showCharges: boolean;
  invoiceReference: string; // Session AHJ: the office's own Xero number, used when charges are hidden
  tier: CalloutReportTier;
  // Session AOJ: the per-visit Core-basis exception for a NON_CONTRACT caller, and the office's
  // stated reason for it. Report-scoped: nothing is written to the customer, so their next call-out
  // starts back at the published non-contract card.
  nonContractBasisOverride: boolean;
  nonContractOverrideReason: string;
  // Session ASW (D22): drop the matched contract rate carried from the emergency intake (see the notice in the charges).
  dropMatchedContractRate: boolean;
  // Session ASM: the one-off price agreed for THIS attendance, typed in POUNDS (parsed to pence at the boundary, as
  // the log and emergency intakes do), and why. Blank keeps the computed card. Zero is a real decision and still
  // raises a GBP 0.00 draft.
  agreedPrice: string;
  agreedPriceReason: string;
  descFirstHour: string;
  descAdditional: string;
  descTravel: string;
  clientSignatoryName: string;
}

export interface CalloutReportInitial {
  calloutId?: string | null;
  reportUrl?: string | null;
  // Session AHJ: whether a report PDF is actually filed in Drive, i.e. whether the authenticated
  // download route has something to serve. Distinct from reportUrl, which is the Drive link.
  hasReportPdf?: boolean;
  // Session AHJ: the reference this call-out's report is CURRENTLY issued under, which is not
  // always what the stored form would reproduce (it can have been corrected since). Display only:
  // see keptReferenceLabel, and the server's keptReportRef which is what actually keeps it.
  currentReportRef?: string | null;
  // Session AJW: whether (and to whom/when) the report has already been emailed to the customer.
  reportSentAt?: string | null;
  reportSentTo?: string | null;
  form?: Partial<CalloutReportInput> & { customerMode?: "existing" | "oneoff"; siteId?: string | null };
  // Session ASW (D22): set when the office billed this emergency call-out at a matched customer's contract rate at
  // intake: the customer's name. The report carries that rate unless the office drops it here.
  matchedContractRateName?: string | null;
}

// Whether a REGENERATE has an existing reference to keep, and what it reads as (Session AHJ).
//
// The field is deliberately NOT pre-filled from it. An earlier cut pre-filled, and the adversarial
// review found that it cannot be made correct: pre-filling from the stored form reverts a corrected
// typo, pre-filling from the current reference puts a value into a field whose own rule is
// INV-<digits> and so hard-blocks a regenerate whenever the reference was corrected to something
// else (a credit note, which PATCH /:id/report-ref deliberately allows). The two pull in opposite
// directions.
//
// The record is the authority, so the SERVER keeps the existing reference when the form sends none
// (keptReportRef). The field is therefore blank on a regenerate and means "change it to this",
// which is also the only thing its INV-<digits> rule should be judging. This function exists to
// TELL the office what will be kept if they leave it alone.
//
// The platform's own fallback is not worth naming: it is what the office is trying to get away
// from, and the comparison is exact against reportRefFallback's output rather than a guess.
export function keptReferenceLabel(input: { currentReportRef?: string | null; calloutId?: string | null }): string | null {
  const current = input.currentReportRef?.trim();
  if (!current || !input.calloutId) return null;
  return current === `CO-${input.calloutId}` ? null : current;
}

function blankForm(): FormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    calloutId: null,
    customerMode: "existing",
    customerId: null,
    siteId: null,
    assetId: null,
    oneOffCompany: "",
    oneOffContact: "",
    oneOffAddress: "",
    oneOffEmail: "",
    oneOffPhone: "",
    status: "service",
    siteName: "",
    siteAddress: "",
    liftDesignation: "",
    liftType: "",
    locationInBuilding: "",
    manufacturer: "",
    serialNumber: "",
    controllerDrive: "",
    dateReported: today,
    dateAttended: today,
    reportedBy: "",
    natureOfCallout: "",
    timeOn: "",
    timeOff: "",
    travelHours: 0,
    addlHours: 0,
    oohOverride: null,
    faultReported: "",
    findingsAndCause: "",
    workCarriedOut: "",
    partsUsed: "",
    partsOnOrder: "",
    furtherWorks: "",
    additionalComments: "",
    showCharges: true,
    invoiceReference: "",
    tier: "core",
    // Session AOJ: UNTICKED always. Every report for a non-contract caller bills at the published
    // card unless the office actively grants the exception on that one report.
    nonContractBasisOverride: false,
    nonContractOverrideReason: "",
    dropMatchedContractRate: false,
    agreedPrice: "",
    agreedPriceReason: "",
    descFirstHour: "",
    descAdditional: "",
    descTravel: "",
    clientSignatoryName: "",
  };
}

function fromInitial(initial: CalloutReportInitial | null | undefined): FormState {
  const base = blankForm();
  if (!initial?.form) return { ...base, calloutId: initial?.calloutId ?? null };
  const f = initial.form;
  const oneOff = f.oneOff ?? null;
  return {
    ...base,
    calloutId: initial.calloutId ?? f.calloutId ?? null,
    customerMode: f.customerMode ?? (oneOff && !f.customerId ? "oneoff" : "existing"),
    customerId: f.customerId ?? null,
    siteId: f.siteId ?? null,
    assetId: f.assetId ?? null,
    oneOffCompany: oneOff?.companyName ?? "",
    oneOffContact: oneOff?.contactName ?? "",
    oneOffAddress: oneOff?.address ?? "",
    oneOffEmail: oneOff?.email ?? "",
    oneOffPhone: oneOff?.phone ?? "",
    status: f.status ?? base.status,
    siteName: f.siteName ?? "",
    siteAddress: f.siteAddress ?? "",
    liftDesignation: f.liftDesignation ?? "",
    liftType: f.liftType ?? "",
    locationInBuilding: f.locationInBuilding ?? "",
    manufacturer: f.manufacturer ?? "",
    serialNumber: f.serialNumber ?? "",
    controllerDrive: f.controllerDrive ?? "",
    dateReported: f.dateReported ?? base.dateReported,
    dateAttended: f.dateAttended ?? base.dateAttended,
    reportedBy: f.reportedBy ?? "",
    natureOfCallout: f.natureOfCallout ?? "",
    timeOn: f.timeOn ?? "",
    timeOff: f.timeOff ?? "",
    travelHours: f.travelHours ?? 0,
    addlHours: f.addlHours ?? 0,
    oohOverride: f.isOOH ?? null,
    faultReported: f.faultReported ?? "",
    findingsAndCause: f.findingsAndCause ?? "",
    workCarriedOut: f.workCarriedOut ?? "",
    partsUsed: f.partsUsed ?? "",
    partsOnOrder: f.partsOnOrder ?? "",
    furtherWorks: f.furtherWorks ?? "",
    additionalComments: f.additionalComments ?? "",
    showCharges: f.showCharges ?? true,
    // Deliberately blank on a regenerate: see keptReferenceLabel. The server keeps the reference
    // the report is already issued under when this is not sent, so an empty field means "keep it",
    // and anything typed here means "change it to this".
    invoiceReference: "",
    tier: f.tier ?? (oneOff && !f.customerId ? "non-contract" : "core"),
    // Session AOJ: a REGENERATE keeps the exception it was issued under. Dropping it here would
    // silently re-price the visit at the published card the moment the office corrected a typo in
    // the narrative, handing the customer a second document with a different total for one visit.
    // (The stored tier already carries the Core basis, so the charge itself would survive either
    // way; this keeps the CONTROL and its reason showing what was actually agreed.)
    nonContractBasisOverride: f.nonContractBasisOverride ?? false,
    nonContractOverrideReason: f.nonContractOverrideReason ?? "",
    // Session ASM: a regenerate keeps the agreed price it was issued under, for the same reason as the exception above.
    agreedPrice: typeof f.agreedPricePence === "number" ? (f.agreedPricePence / 100).toFixed(2) : "",
    agreedPriceReason: f.agreedPriceReason ?? "",
    descFirstHour: f.lineDescriptions?.firstHour ?? "",
    descAdditional: f.lineDescriptions?.additional ?? "",
    descTravel: f.lineDescriptions?.travel ?? "",
    clientSignatoryName: f.clientSignatoryName ?? "",
  };
}

function downloadBase64Pdf(base64: string, filename: string): void {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function CalloutReportModal({
  initial,
  onClose,
  onGenerated,
  onReleased,
}: {
  initial?: CalloutReportInitial | null;
  onClose: () => void;
  onGenerated?: () => void;
  // Session AJW; renamed Session AQY (D-AQX-3) from onSent: bubbled after a successful RELEASE so
  // the row behind this modal (which is not otherwise reloaded on close) can show "Released"
  // without a full list refetch.
  onReleased?: (releasedAt: string, releasedTo: string) => void;
}) {
  const [form, setForm] = useState<FormState>(() => fromInitial(initial));
  // Session ASQ (Stream R, R4): whose call-out this form is bound to, i.e. the record a Generate will update. Seeded from
  // the call-out the modal was opened on, and moved to the customer a successful Generate used, so a report generated in
  // this same modal and then pointed at another customer is recognised as a move too. The server refuses a report that
  // names a customer other than the call-out's own unless the office moves it deliberately (moveToCustomer below).
  const [bound, setBound] = useState<{ customerId: string | null; assetId: string | null }>(() => ({
    customerId: initial?.calloutId || initial?.form?.calloutId ? (initial?.form?.customerId ?? null) : null,
    assetId: initial?.form?.assetId ?? null,
  }));
  // Session ASQ (Stream R, R6): the agreed price the call-out already carried when the modal opened (the Hub and the
  // Call-outs list seed it from the record), so the office is told it will be billed, and what clearing it does.
  const [carriedAgreedPence] = useState<number | null>(() =>
    (initial?.calloutId || initial?.form?.calloutId) && typeof initial?.form?.agreedPricePence === "number" ? initial.form.agreedPricePence : null,
  );
  // Session SC2B: the customer, site and lift are SEARCHED (typeahead pickers), not chosen from loaded registers. The customer record of the
  // chosen customer (its type decides the non-contract basis) is kept from the pick, or read once by id when the form opens on one.
  const [knownCustomers, setKnownCustomers] = useState<Record<string, Pick<Customer, "id" | "name" | "customerType">>>({});
  const [customerLoadFailed, setCustomerLoadFailed] = useState(false);
  const [preview, setPreview] = useState<CalloutReportPreviewResult | null>(null);
  // A Generate that would replace the call-out's recorded charge with a different figure is refused by the server
  // until the office confirms it (storedChargeChange.ts). The server's own sentence is held here and Generate is
  // offered again behind a two-step confirm.
  const [chargeChangeRefusal, setChargeChangeRefusal] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CalloutReportResult | null>(null);
  const [savedReportUrl] = useState<string | null>(initial?.reportUrl ?? null);
  // Session AHJ: the call-out id to fetch the filed PDF by, when one is filed. Null on a fresh
  // report (nothing filed yet); the just-generated PDF has already downloaded in that case.
  const [savedReportPdfId] = useState<string | null>(initial?.hasReportPdf && initial?.calloutId ? initial.calloutId : null);
  const [saveToDrive, setSaveToDrive] = useState(true);
  // Session AJW; repurposed Session AQY (D-AQX-3): release state, seeded from the record and updated
  // in place after a successful release (unlike savedReportPdfId/savedReportUrl above, this one
  // genuinely changes within the modal's own lifetime). The record's own field names are unchanged
  // (reportSentAt/reportSentTo), only what stamping them now MEANS.
  const [releasedAt, setReleasedAt] = useState<string | null>(initial?.reportSentAt ?? null);
  const [releasedTo, setReleasedTo] = useState<string | null>(initial?.reportSentTo ?? null);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseNote, setReleaseNote] = useState<string | null>(null);
  // "Not in the platform?" inline one-off caller creation (Session AFM). The created customer is
  // held locally: the customers prop was fetched before it existed.
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Session ASQ, Stream G, Item 3: the email typed belongs to a person whose data was erased. The server's own sentence,
  // and "Create anyway" resends with createAnyway (audited).
  const [createErased, setCreateErased] = useState<string | null>(null);
  const [createdCustomer, setCreatedCustomer] = useState<Customer | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const selectedCustomer = useMemo(
    () => (form.customerId ? (knownCustomers[form.customerId] ?? (createdCustomer && createdCustomer.id === form.customerId ? createdCustomer : null)) : null),
    [knownCustomers, form.customerId, createdCustomer],
  );
  // Read a customer by id when the form opens on one (an existing call-out) or when the bound customer is needed by name.
  useEffect(() => {
    const need = [form.customerId, bound.customerId].filter((id): id is string => !!id && !knownCustomers[id] && createdCustomer?.id !== id);
    for (const id of new Set(need)) {
      apiRequest<Customer>(`/customers/${encodeURIComponent(id)}`)
        .then((c) => setKnownCustomers((k) => ({ ...k, [id]: { id, name: c.name, customerType: c.customerType } })))
        .catch(() => {
          if (id === form.customerId) setCustomerLoadFailed(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customerId, bound.customerId]);
  // A NON_CONTRACT caller has no tier BY CLASSIFICATION: the tier selector is replaced by a fixed
  // basis notice and the server forces non-contract regardless.
  const nonContractCustomer = selectedCustomer?.customerType === "NON_CONTRACT";
  // Session AOJ. The exception is only ever LIVE for a non-contract caller, so a tick left behind by
  // switching customer can never quietly re-price the next one. This is the one value the preview,
  // the submitted body and the notice in the charges panel all read, so the three cannot disagree.
  //
  // NOTE FOR WHOEVER LANDS THE REPAIR TOGGLE (attendanceType on this form, currently server-side
  // only and always "callout" from here): gate this off that field too. The exception must never
  // reach a repair, which already takes no first-hour premium and no tier discount at any tier, so
  // granting it there would simply halve the non-contract multiplier. The SERVER already refuses
  // that case (resolveReportBasis) and the preview comes back nonContractBasisOverridden false, but
  // the control should not offer itself at all.
  const nonContractOverrideActive = nonContractCustomer && form.nonContractBasisOverride;
  // The client half of a defence in depth: the server enforces the identical rule
  // (nonContractOverrideReasonError), and this tells the office while they are typing rather than
  // with a 400 after a long form has been filled in. Mirrors RaiseWorkModal's priceReasonMissing.
  const overrideReasonMissing = nonContractOverrideActive && form.nonContractOverrideReason.trim().length === 0;

  // Out of hours: auto-derived from the attendance date + time on, overridable by the toggle.
  const autoOoh = useMemo(() => {
    if (!form.timeOn || !form.dateAttended) return false;
    const d = new Date(`${form.dateAttended}T${form.timeOn}`);
    return Number.isNaN(d.getTime()) ? false : deriveOutOfHours(d);
  }, [form.timeOn, form.dateAttended]);
  const effectiveOoh = form.oohOverride ?? autoOoh;

  // Whether the office has manually chosen a tier: once true, the server-resolved default tier is
  // never auto-adopted over their choice. Reset on any new customer/lift selection.
  const tierTouched = useRef(false);

  function pickCustomer(c: Pick<Customer, "id" | "name" | "customerType">) {
    tierTouched.current = false;
    setKnownCustomers((k) => ({ ...k, [c.id]: { id: c.id, name: c.name, customerType: c.customerType } }));
    setCustomerLoadFailed(false);
    // A NON_CONTRACT caller's basis is fixed; everyone else starts at Core until the
    // server-resolved default tier is adopted.
    const tier: CalloutReportTier = c.customerType === "NON_CONTRACT" ? "non-contract" : "core";
    setForm((f) => ({
      ...f,
      customerId: c.id,
      siteId: null,
      assetId: null,
      siteName: "",
      siteAddress: "",
      tier,
      // Session AOJ: the exception belongs to ONE caller. The nonContractOverrideActive gate already
      // stops a stale tick pricing a CONTRACT customer, but it cannot stop one carrying from one
      // non-contract caller to the next, where the tick would stay live and the stored reason would
      // name the wrong customer. Cleared wherever the customer identity changes, alongside the
      // tierTouched reset that exists for the same class of reason.
      nonContractBasisOverride: false,
      nonContractOverrideReason: "",
    }));
  }

  // "Not in the platform?" (Session AFM): create a real NON_CONTRACT Customer (server also creates
  // its Site and attempts the Xero contact), then proceed as an existing customer with the site
  // display fields pre-filled from what was just typed. The record makes the caller findable,
  // promotable and cleanly invoiced on every future call-out.
  async function createOneOffCaller(createAnyway = false) {
    const name = form.oneOffCompany.trim();
    const siteAddress = form.oneOffAddress.trim();
    if (!name || !siteAddress) return;
    setCreateBusy(true);
    setCreateError(null);
    setCreateErased(null);
    try {
      const created = (await customersResource.create({
        name,
        customerType: "NON_CONTRACT",
        siteAddress,
        billingContact: form.oneOffContact.trim() || undefined,
        billingEmail: form.oneOffEmail.trim() || undefined,
        phone: form.oneOffPhone.trim() || undefined,
        ...(createAnyway ? { createAnyway: true } : {}),
      })) as Customer;
      setCreatedCustomer(created);
      tierTouched.current = false;
      setForm((f) => ({
        ...f,
        customerMode: "existing",
        customerId: created.id,
        siteId: null,
        assetId: null,
        siteName: name,
        siteAddress,
        tier: "non-contract",
      // Session AOJ: the exception belongs to ONE caller. The nonContractOverrideActive gate already
      // stops a stale tick pricing a CONTRACT customer, but it cannot stop one carrying from one
      // non-contract caller to the next, where the tick would stay live and the stored reason would
      // name the wrong customer. Cleared wherever the customer identity changes, alongside the
      // tierTouched reset that exists for the same class of reason.
      nonContractBasisOverride: false,
      nonContractOverrideReason: "",
      }));
      setCreateOpen(false);
    } catch (err) {
      const erased = erasedPersonRefusal(err);
      if (erased) setCreateErased(erased.message);
      else setCreateError(err instanceof ApiError ? err.message || err.code : "The caller could not be created. Please try again.");
    } finally {
      setCreateBusy(false);
    }
  }
  // Clearing the selected customer must also clear the site/lift it filled, so a later one-off or a
  // different customer never inherits stale site data (AEY review finding).
  function changeCustomer() {
    tierTouched.current = false;
    setForm((f) => ({
      ...f,
      customerId: null,
      siteId: null,
      assetId: null,
      siteName: "",
      siteAddress: "",
      liftDesignation: "",
      liftType: "",
      locationInBuilding: "",
      manufacturer: "",
      serialNumber: "",
      controllerDrive: "",
      // Session AOJ: the exception belongs to ONE caller. The nonContractOverrideActive gate already
      // stops a stale tick pricing a CONTRACT customer, but it cannot stop one carrying from one
      // non-contract caller to the next, where the tick would stay live and the stored reason would
      // name the wrong customer. Cleared wherever the customer identity changes, alongside the
      // tierTouched reset that exists for the same class of reason.
      nonContractBasisOverride: false,
      nonContractOverrideReason: "",
    }));
  }
  // Switching customer mode clears the OTHER mode's selection + the site/lift display fields (they
  // belong to a specific selection) and sets the correct default tier (non-contract for a one-off).
  function switchMode(mode: "existing" | "oneoff") {
    tierTouched.current = false;
    setForm((f) => ({
      ...f,
      // Session AOJ: the exception belongs to ONE caller. The nonContractOverrideActive gate already
      // stops a stale tick pricing a CONTRACT customer, but it cannot stop one carrying from one
      // non-contract caller to the next, where the tick would stay live and the stored reason would
      // name the wrong customer. Cleared wherever the customer identity changes, alongside the
      // tierTouched reset that exists for the same class of reason.
      nonContractBasisOverride: false,
      nonContractOverrideReason: "",
      customerMode: mode,
      customerId: mode === "oneoff" ? null : f.customerId,
      siteId: null,
      assetId: null,
      siteName: "",
      siteAddress: "",
      liftDesignation: "",
      liftType: "",
      locationInBuilding: "",
      manufacturer: "",
      serialNumber: "",
      controllerDrive: "",
      tier: mode === "oneoff" ? "non-contract" : "core",
    }));
  }
  // The picked site / lift arrive with their row (the picker record), which is all the form reads of them.
  const text = (v: unknown): string => (typeof v === "string" ? v : "");
  function pickSite(siteId: string, rec: PickerRecord | null) {
    const row = rec?.raw ?? {};
    setForm((f) => ({ ...f, siteId: siteId || null, assetId: null, siteName: text(row.name), siteAddress: text(row.address), liftDesignation: "", liftType: "", manufacturer: "", serialNumber: "", controllerDrive: "" }));
  }
  function pickLift(assetId: string, rec: PickerRecord | null) {
    tierTouched.current = false; // a new lift can have a different coverage tier
    const a = rec?.raw ?? {};
    setForm((f) => ({
      ...f,
      assetId: assetId || null,
      liftDesignation: text(a.eleNumber) || text(a.legacyEleNumber),
      liftType: text(a.liftType),
      manufacturer: text(a.manufacturer),
      serialNumber: text(a.serial),
      controllerDrive: text(a.driveSystem) || text(a.driveType),
    }));
  }

  // Live, server-authoritative charge preview (no browser money math). Debounced. The customerId is
  // gated by the mode (matching toBody) so a one-off never prices off a stale customer's override.
  const previewCustomerId = form.customerMode === "existing" ? form.customerId : null;
  const previewKey = `${form.showCharges}|${form.tier}|${form.addlHours}|${form.travelHours}|${effectiveOoh}|${previewCustomerId ?? ""}|${form.assetId ?? ""}|${form.customerMode}|${nonContractOverrideActive}|${form.dropMatchedContractRate}`;
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!form.showCharges) {
      setPreview(null);
      return;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      api.callouts
        .reportPreview({ customerId: previewCustomerId, assetId: form.customerMode === "existing" ? form.assetId : null, tier: form.tier, addlHours: form.addlHours, travelHours: form.travelHours, isOOH: effectiveOoh, nonContractBasisOverride: nonContractOverrideActive, calloutId: form.calloutId ?? null, dropMatchedContractRate: form.dropMatchedContractRate })
        .then((p) => {
          setPreview(p);
          // Adopt the server-resolved default tier (Care/Complete) for an existing customer + lift,
          // unless the office has picked a tier. Functional update reads the latest form/tier and
          // settles after one adopt (previewKey re-runs with the adopted tier, which then matches).
          setForm((f) => {
            if (!tierTouched.current && p.defaultTier && p.defaultTier !== f.tier && f.customerMode === "existing" && f.customerId) {
              return { ...f, tier: p.defaultTier };
            }
            return f;
          });
        })
        .catch(() => setPreview(null));
    }, 300);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // Session AHJ: the manual reference is refused while it is being typed rather than after a long
  // form is submitted. Only meaningful when charges are hidden, which is the only case it is shown
  // or sent in, so a stale value left behind by toggling charges back on never blocks Generate.
  const invoiceRefError = form.showCharges ? null : manualInvoiceRefError(form.invoiceReference);
  // What the server will keep if the field is left blank on a regenerate (Session AHJ).
  const keptReference = keptReferenceLabel({ currentReportRef: initial?.currentReportRef, calloutId: form.calloutId });

  // Session ASM: the agreed price, parsed exactly as the intakes parse theirs. Only read while charges are shown (it
  // decides the draft), and required to carry a reason the moment a valid figure is typed, zero included.
  const agreed = form.showCharges ? parseAgreedPrice(form.agreedPrice) : ({ state: "blank", pence: null } as const);
  const agreedReasonMissing = agreed.state === "ok" && form.agreedPriceReason.trim().length === 0;

  // Session ASQ (Stream R, R4): the office changed the customer on a report about an EXISTING call-out. A call-out with no
  // lift MOVES to the new customer (the call-out, its visit in the service history and its draft go with it), and the
  // form says so before Generate; one on a lift cannot move, because its customer is the lift's own, so Generate is held
  // back with the reason rather than refused by the server after a long form.
  const customerChanged =
    Boolean(form.calloutId) && form.customerMode === "existing" && Boolean(bound.customerId) && Boolean(form.customerId) && form.customerId !== bound.customerId;
  const movingCustomer = customerChanged && !bound.assetId;
  const moveBlocked = customerChanged && Boolean(bound.assetId);
  const boundCustomerName = bound.customerId ? (knownCustomers[bound.customerId]?.name ?? (createdCustomer?.id === bound.customerId ? createdCustomer.name : null)) : null;

  const canGenerate =
    (form.customerMode === "existing" ? Boolean(form.customerId) : Boolean(form.oneOffCompany.trim())) &&
    Boolean(form.siteName.trim() || form.siteAddress.trim()) &&
    !invoiceRefError &&
    !overrideReasonMissing &&
    agreed.state !== "invalid" &&
    !agreedReasonMissing &&
    !moveBlocked;

  function toBody(): CalloutReportInput {
    return {
      calloutId: form.calloutId,
      customerId: form.customerMode === "existing" ? form.customerId : null,
      assetId: form.customerMode === "existing" ? form.assetId : null,
      oneOff:
        form.customerMode === "oneoff"
          ? { companyName: form.oneOffCompany, contactName: form.oneOffContact, address: form.oneOffAddress, email: form.oneOffEmail, phone: form.oneOffPhone }
          : null,
      status: form.status,
      siteName: form.siteName,
      siteAddress: form.siteAddress,
      liftDesignation: form.liftDesignation,
      liftType: form.liftType,
      locationInBuilding: form.locationInBuilding,
      manufacturer: form.manufacturer,
      serialNumber: form.serialNumber,
      controllerDrive: form.controllerDrive,
      dateReported: form.dateReported,
      dateAttended: form.dateAttended,
      reportedBy: form.reportedBy,
      natureOfCallout: form.natureOfCallout,
      timeOn: form.timeOn,
      timeOff: form.timeOff,
      travelHours: form.travelHours,
      addlHours: form.addlHours,
      isOOH: effectiveOoh,
      faultReported: form.faultReported,
      findingsAndCause: form.findingsAndCause,
      workCarriedOut: form.workCarriedOut,
      partsUsed: form.partsUsed,
      partsOnOrder: form.partsOnOrder,
      furtherWorks: form.furtherWorks,
      additionalComments: form.additionalComments,
      showCharges: form.showCharges,
      // Session AHJ. Sent only when charges are hidden, which is the only case the server reads it
      // in: sending it otherwise would persist a reference on the stored form that a later
      // regenerate would pre-fill and that nothing ever uses.
      invoiceReference: form.showCharges ? null : form.invoiceReference.trim().toUpperCase() || null,
      // Belt and braces: a NON_CONTRACT caller always submits the non-contract basis (the server
      // forces it regardless of what the form held).
      tier: nonContractCustomer ? "non-contract" : form.tier,
      // Session AOJ. The tier above deliberately still says "non-contract": the classification has
      // not changed, and the server resolves the exception from THIS flag rather than from a tier
      // the form claimed, so "the server never trusts the form" holds exactly as it did.
      nonContractBasisOverride: nonContractOverrideActive,
      nonContractOverrideReason: nonContractOverrideActive ? form.nonContractOverrideReason.trim() : null,
      // Session ASW (D22): the server carries the intake's matched contract rate unless this says to drop it.
      dropMatchedContractRate: form.dropMatchedContractRate,
      // Session ASM. Null (not omitted) when blank, so a regenerate that clears the field is heard as "no agreed price".
      // Session ASQ (Stream R, R6): the server now keeps a price the call-out carries unless it is explicitly cleared, and
      // this field is SEEDED with that price, so a blank field here is the office's deliberate clear. With charges hidden
      // the field is not shown at all, so nothing is sent and the call-out's price is left exactly as it is.
      ...(form.showCharges
        ? { agreedPricePence: agreed.state === "ok" ? agreed.pence : null, agreedPriceReason: agreed.state === "ok" ? form.agreedPriceReason.trim() : null }
        : {}),
      lineDescriptions: { firstHour: form.descFirstHour || undefined, additional: form.descAdditional || undefined, travel: form.descTravel || undefined },
      clientSignatoryName: form.clientSignatoryName,
      // Session ASQ (Stream R, R4 and R3): the deliberate move, sent only when the office changed the customer on a report
      // about an existing lift-less call-out. The new customer's place goes with it as an explicit siteLabel, because
      // on a report about an existing call-out only an explicit label moves the visit in the service history.
      ...(movingCustomer ? { moveToCustomer: true, siteLabel: form.siteName.trim() || form.siteAddress.trim() || null } : {}),
    };
  }

  async function generate(confirmChargeChange = false) {
    setGenerating(true);
    setError(null);
    setResult(null);
    const body = { ...toBody(), ...(confirmChargeChange ? { confirmChargeChange: true } : {}) };
    // Whose record the call-out is after this Generate: the customer (and, for a fresh report, the lift) it was sent for.
    const bindTo = () => setBound((b) => ({ customerId: body.customerId ?? null, assetId: b.assetId ?? body.assetId ?? null }));
    try {
      const res = await api.callouts.generateReport(body, saveToDrive);
      // Bind the form to the server-created/updated call-out so a REGENERATE targets that same
      // record (entry point 1) instead of creating a SECOND CallOut + a SECOND Xero draft for the
      // same attendance (AEY review finding).
      setForm((f) => ({ ...f, calloutId: res.calloutId }));
      bindTo();
      setChargeChangeRefusal(null);
      setResult(res);
      downloadBase64Pdf(res.pdfBase64, res.filename);
      onGenerated?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === "charge_change_unconfirmed") {
        setChargeChangeRefusal(typeof err.body?.message === "string" ? err.body.message : "This would replace the call-out's recorded charge with a different figure.");
        return;
      }
      // Session ASQ (Stream R, N2): a PDF render failure happens AFTER the call-out, its visit and its draft were written,
      // and the server names that call-out. Bind to it here too, so pressing Generate again finishes THAT record (one
      // call-out, one visit, one live draft) rather than creating a second of each.
      const writtenId = err instanceof ApiError && typeof err.body?.calloutId === "string" ? err.body.calloutId : null;
      if (writtenId) {
        setForm((f) => ({ ...f, calloutId: writtenId }));
        bindTo();
      }
      const message = err instanceof ApiError ? err.message || err.code : "The report could not be generated. Please try again.";
      setError(writtenId ? `${message} The call-out is saved, so pressing Generate again finishes that same record.` : message);
    } finally {
      setGenerating(false);
    }
  }

  // Session AJW; rebuilt Session AQY (D-AQX-3): RELEASE the FILED PDF -- opens portal visibility,
  // drafts (never sends) the completion email to Gmail, and drafts the NPS survey too where this
  // call-out is eligible. Uses form.calloutId (not initial?.calloutId) so a release right after a
  // fresh Generate targets the record that generate() just bound the form to, not a stale id from
  // before this modal opened.
  async function releaseReportNow() {
    if (!form.calloutId) return;
    setReleasing(true);
    setReleaseError(null);
    try {
      const res = await api.callouts.releaseReport(form.calloutId);
      setReleasedAt(res.releasedAt);
      setReleasedTo(res.releasedTo);
      setReleaseNote([res.draftNote, res.npsNote].filter(Boolean).join(" ") || null);
      onReleased?.(res.releasedAt, res.releasedTo);
    } catch (err) {
      setReleaseError(err instanceof ApiError ? err.message || err.code : "The report could not be released. Please try again.");
    } finally {
      setReleasing(false);
    }
  }

  const busy = generating;

  return (
    <ModalShell ariaLabel="Call-out attendance report" onClose={() => !busy && onClose()} width="max-w-3xl" panelClassName="max-h-[92vh] overflow-y-auto p-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">Call-Out Attendance Report</h2>
          <p className="text-sm text-ele-body">Record a reactive attendance, generate the PDF and raise a Xero draft.</p>
        </div>
        {/* Session AHJ: the filed PDF opens straight from here, through the authenticated proxy,
            instead of the office going to Drive to find it first. The Drive link is kept beside it
            (it is the record's home, and shows the office where it was filed).
            Session AJW; rebuilt Session AQY (D-AQX-3): the release state leads, ahead of the PDF
            links -- this is the first thing visible on opening the modal (before the whole edit
            form below), so "click into the report and see whether it has been released" needs no
            scrolling. Release drafts the email rather than sending it, so the label says so. */}
        <div className="flex flex-wrap items-center gap-3">
          {savedReportPdfId && (
            releasedAt ? (
              <StatusPill word={`Released ${formatDate(releasedAt)}`} tone="green" title={releasedTo ? `Released to ${releasedTo}` : undefined} />
            ) : (
              <span className="inline-flex items-center gap-2">
                <StatusPill word="Not yet released" tone="amber" />
                <ConfirmButton
                  onConfirm={releaseReportNow}
                  label="Release"
                  confirmLabel="Confirm? Drafts the customer email"
                  busy={releasing}
                  busyLabel="Releasing..."
                  title="Opens this report on the customer portal and drafts the completion email (and, where eligible, the survey) to Gmail for the office to review and send."
                  className={buttonClasses("primary", "sm")}
                />
              </span>
            )
          )}
          {savedReportPdfId && (
            <a
              href={api.callouts.reportPdfHref(savedReportPdfId)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-menu text-xs tracking-wide text-ele-green underline-offset-2 hover:underline"
            >
              Open PDF
            </a>
          )}
          {savedReportUrl && (
            <a href={savedReportUrl} target="_blank" rel="noopener noreferrer" className="font-menu text-xs tracking-wide text-ele-body underline-offset-2 hover:underline">
              View in Drive
            </a>
          )}
        </div>
      </div>
      {releaseError && (
        <p role="alert" className="mb-3 -mt-2 text-sm text-ele-danger">
          {releaseError}
        </p>
      )}
      {releaseNote && (
        <p className="mb-3 -mt-2 text-sm text-ele-body">
          {releaseNote}
        </p>
      )}

      {/* Customer resolution */}
      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
        {form.customerMode === "existing" ? (
          selectedCustomer ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-heading text-ele-strong">{selectedCustomer.name}</span>
                {nonContractCustomer && <span className="text-xs text-ele-body opacity-70">(non-contract)</span>}
                <button type="button" onClick={changeCustomer} className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
                  Change
                </button>
              </div>
              {/* Session ASQ (Stream R, R4): changing the customer on an existing call-out moves it, and says so first. */}
              {movingCustomer && (
                <p role="status" className="mt-2 text-xs text-ele-strong">
                  {`This report moves to ${selectedCustomer.name}. The call-out, its visit in the service history and its draft invoice all go with it${boundCustomerName ? `, off ${boundCustomerName}'s record` : ""}.`}
                </p>
              )}
              {moveBlocked && (
                <p role="alert" className="mt-2 text-xs text-ele-danger">
                  {`This call-out is for a lift on ${boundCustomerName ?? "another customer"}'s account, so its report cannot move to ${selectedCustomer.name}. Change the customer back, or log a new call-out for this customer.`}
                </p>
              )}
            </>
          ) : (
            <>
              {form.customerId ? (
                <p className="text-sm text-ele-body" role={customerLoadFailed ? "alert" : "status"}>
                  {customerLoadFailed ? "Could not load this customer. Change the customer and pick it again." : "Loading the customer..."}
                  {customerLoadFailed && (
                    <button type="button" onClick={changeCustomer} className="ml-3 font-menu text-xs tracking-wide text-ele-blue hover:underline">
                      Change
                    </button>
                  )}
                </p>
              ) : (
                <RecordPicker
                  kind="customer"
                  value=""
                  ariaLabel="Search customers by name"
                  placeholder="Search customers by name"
                  filters={{ customerType: "all" }}
                  onChange={(id, rec) => {
                    if (!id || !rec) return;
                    const t = rec.raw.customerType;
                    pickCustomer({ id, name: rec.label, customerType: t === "NON_CONTRACT" ? "NON_CONTRACT" : "CONTRACT" });
                  }}
                />
              )}

              {/* Session AFM: inline one-off caller creation. Expands in place (not a modal, not a
                  new page); on create the caller becomes the selected customer and the report
                  proceeds normally at the non-contract basis. */}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setCreateOpen((o) => !o)}
                  className="font-menu text-xs tracking-wide text-ele-body underline-offset-2 hover:text-ele-strong hover:underline"
                >
                  Not in the platform?
                </button>
                {createOpen && (
                  <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-inset p-3">
                    <p className="mb-2 text-xs text-ele-body">
                      Create a one-off caller record. They are billed at non-contract rates, kept out of the main customer
                      list, and can be promoted to a full customer later.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Company / caller name*" value={form.oneOffCompany} onChange={(v) => set("oneOffCompany", v)} />
                      <Field label="Site address*" value={form.oneOffAddress} onChange={(v) => set("oneOffAddress", v)} />
                      <Field label="Contact name" value={form.oneOffContact} onChange={(v) => set("oneOffContact", v)} />
                      <Field label="Email" value={form.oneOffEmail} onChange={(v) => set("oneOffEmail", v)} />
                      <Field label="Phone" value={form.oneOffPhone} onChange={(v) => set("oneOffPhone", v)} />
                    </div>
                    {createError && (
                      <p role="alert" className="mt-2 text-sm text-ele-danger">{createError}</p>
                    )}
                    {createErased && (
                      <div role="alert" className="mt-2 rounded-ele border border-amber-400 p-2 text-sm text-ele-strong">
                        <p>{createErased}</p>
                        <button
                          type="button"
                          onClick={() => void createOneOffCaller(true)}
                          disabled={createBusy}
                          className="mt-2 rounded-ele border border-ele-danger px-3 py-1.5 font-menu text-xs tracking-wide text-ele-danger disabled:opacity-50"
                        >
                          {createBusy ? "Creating..." : "Create anyway"}
                        </button>
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={() => void createOneOffCaller()}
                      disabled={createBusy || !form.oneOffCompany.trim() || !form.oneOffAddress.trim()}
                      className="mt-3"
                    >
                      {createBusy ? "Creating..." : "Create one-off caller"}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )
        ) : (
          /* Legacy record-less one-off: reachable only when regenerating a pre-AFM report whose
             caller lives in the CallOut's ad-hoc fields. Fresh one-off entry creates a customer. */
          <div>
            <p className="mb-2 text-xs text-ele-body">One-off caller (no customer record; a pre-AFM report).</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Company name*" value={form.oneOffCompany} onChange={(v) => set("oneOffCompany", v)} />
              <Field label="Contact name" value={form.oneOffContact} onChange={(v) => set("oneOffContact", v)} />
              <Field label="Email" value={form.oneOffEmail} onChange={(v) => set("oneOffEmail", v)} />
              <Field label="Phone" value={form.oneOffPhone} onChange={(v) => set("oneOffPhone", v)} />
              <Field label="Address" value={form.oneOffAddress} onChange={(v) => set("oneOffAddress", v)} className="sm:col-span-2" />
            </div>
            <button type="button" onClick={() => switchMode("existing")} className="mt-2 font-menu text-xs tracking-wide text-ele-blue hover:underline">
              Use an existing customer instead
            </button>
          </div>
        )}

        {/* Existing-customer site + lift pickers */}
        {form.customerMode === "existing" && selectedCustomer && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <RecordPicker
                kind="site"
                id="rep-site-picker"
                label="Site"
                value={form.siteId ?? ""}
                onChange={pickSite}
                filters={{ customerId: form.customerId ?? undefined }}
                selectedLabel={form.siteName || null}
                placeholder="Search this customer's sites"
              />
            </div>
            <div>
              <RecordPicker
                kind="lift"
                id="rep-lift-picker"
                label="Lift"
                value={form.assetId ?? ""}
                onChange={pickLift}
                filters={{ siteId: form.siteId ?? undefined, includeRetired: false }}
                selectedLabel={form.liftDesignation || null}
                disabled={!form.siteId}
                placeholder="Search this site's lifts"
              />
            </div>
          </div>
        )}
      </section>

      {/* Status */}
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        {STATUS_CARDS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => set("status", c.value)}
            className={`rounded-ele border px-3 py-2.5 text-center font-heading text-sm tracking-wide transition ${form.status === c.value ? `${c.ring} ${c.text} bg-ele-inset` : "border-ele-purple-accent text-ele-body hover:text-ele-strong"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Site & lift + call-out details */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <Field label="Site name" value={form.siteName} onChange={(v) => set("siteName", v)} />
        <Field label="Site address" value={form.siteAddress} onChange={(v) => set("siteAddress", v)} />
        <Field label="Lift (ELE number)" value={form.liftDesignation} onChange={(v) => set("liftDesignation", v)} />
        <Field label="Lift type" value={form.liftType} onChange={(v) => set("liftType", v)} />
        <Field label="Location in building" value={form.locationInBuilding} onChange={(v) => set("locationInBuilding", v)} />
        <Field label="Manufacturer" value={form.manufacturer} onChange={(v) => set("manufacturer", v)} />
        <Field label="Serial number" value={form.serialNumber} onChange={(v) => set("serialNumber", v)} />
        <Field label="Controller / drive" value={form.controllerDrive} onChange={(v) => set("controllerDrive", v)} />
        <Field label="Date reported" type="date" value={form.dateReported} onChange={(v) => set("dateReported", v)} />
        <Field label="Date attended" type="date" value={form.dateAttended} onChange={(v) => set("dateAttended", v)} />
        <Field label="Reported by" value={form.reportedBy} onChange={(v) => set("reportedBy", v)} />
        <Field label="Nature of call-out" value={form.natureOfCallout} onChange={(v) => set("natureOfCallout", v)} />
      </div>

      {/* Times */}
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <Field label="Time on" type="time" value={form.timeOn} onChange={(v) => set("timeOn", v)} />
        <Field label="Time off" type="time" value={form.timeOff} onChange={(v) => set("timeOff", v)} />
        <NumberField label="Additional hours" value={form.addlHours} onChange={(v) => set("addlHours", v)} />
        <NumberField label="Travel hours" value={form.travelHours} onChange={(v) => set("travelHours", v)} />
      </div>
      <label className="mb-4 flex items-center gap-2 text-sm text-ele-body">
        <input type="checkbox" checked={effectiveOoh} onChange={(e) => set("oohOverride", e.target.checked)} />
        Out of hours (doubles the labour rate)
      </label>

      {/* Narrative */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <TextArea label="Fault reported" value={form.faultReported} onChange={(v) => set("faultReported", v)} />
        <TextArea label="Findings and probable cause" value={form.findingsAndCause} onChange={(v) => set("findingsAndCause", v)} />
        <TextArea label="Rectification and work carried out" value={form.workCarriedOut} onChange={(v) => set("workCarriedOut", v)} />
        <TextArea label="Further works recommended" value={form.furtherWorks} onChange={(v) => set("furtherWorks", v)} />
        <Field label="Parts used" value={form.partsUsed} onChange={(v) => set("partsUsed", v)} />
        <Field label="Parts on order" value={form.partsOnOrder} onChange={(v) => set("partsOnOrder", v)} />
        <TextArea label="Additional comments" value={form.additionalComments} onChange={(v) => set("additionalComments", v)} className="sm:col-span-2" />
      </div>

      {/* Charges (Session APF: this section, and its checkbox, decide the Xero DRAFT only -- the
          report PDF itself never shows a price, whatever this is set to). */}
      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
        <label className="mb-1 flex items-center gap-2 text-sm text-ele-strong">
          <input type="checkbox" checked={form.showCharges} onChange={(e) => set("showCharges", e.target.checked)} />
          Raise a DRAFT invoice for this visit
        </label>
        <p className="mb-3 text-xs text-ele-body opacity-70">
          The report itself never shows a price. The invoice is where the charge is stated.
        </p>
        {form.showCharges && (
          <>
            {/* Session ASW (D22, review H1): an emergency call-out the office billed at a matched customer's contract
                rate at intake keeps that rate on its report, so the report never re-bills it on the doubled card and
                voids the draft the caller was quoted. The office can drop it deliberately here. */}
            {initial?.matchedContractRateName && form.calloutId && (
              <div className="mb-3 rounded-ele border border-amber-500 bg-amber-500/10 p-3 text-sm" role="status">
                <p className="text-ele-body">
                  {form.dropMatchedContractRate
                    ? "This report bills at the rate the customer's type gives, not the contract rate applied at intake."
                    : `At intake this call-out was billed at ${initial.matchedContractRateName}'s contract rate (the Core in-contract rate). A call-out report for a caller with no contract of their own keeps it. A repair, or a report issued to a contract customer, bills on that report's own terms.`}
                </p>
                <label className="mt-2 flex items-start gap-2 text-ele-body">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.dropMatchedContractRate}
                    onChange={(e) => set("dropMatchedContractRate", e.target.checked)}
                  />
                  <span>Drop the contract rate applied at intake</span>
                </label>
              </div>
            )}
            {nonContractCustomer ? (
              /* Session AFM: no tier exists on a non-contract job. The selector is replaced by a
                 fixed basis notice; the server forces non-contract whatever the form held.

                 Session AOJ: the office can grant a SINGLE exception to that, for this one visit.
                 The pill stays whatever they choose, because the classification is still true and
                 the exception is a stated departure from it rather than a reclassification: the
                 customer is still non-contract, and their next call-out starts back at the card.
                 What the pill CANNOT keep saying is that double rates apply, so the sentence beside
                 it moves with the tick. */
              <div className="mb-3">
                <p className="text-sm text-ele-body">
                  <StatusPill word="Non-contract" tone="blue" />
                  <span className="ml-2">
                    {nonContractOverrideActive
                      ? "Billed at the Core in-contract rate for this visit only."
                      : "Double Schedule of Fees rates apply. No tier."}
                  </span>
                </p>
                <label className="mt-2 flex items-start gap-2 text-sm text-ele-body">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form.nonContractBasisOverride}
                    onChange={(e) => set("nonContractBasisOverride", e.target.checked)}
                  />
                  <span>
                    Bill this one visit at the Core in-contract rate instead
                    <span className="block text-xs opacity-70">
                      This report only. Nothing changes on the customer, and their next call-out prices at the published non-contract card again.
                    </span>
                  </span>
                </label>
                {nonContractOverrideActive && (
                  <div className="mt-2">
                    <label className="block text-xs font-menu uppercase tracking-wide text-ele-body" htmlFor="nc-override-reason">
                      Reason for the Core rate
                    </label>
                    <input
                      id="nc-override-reason"
                      value={form.nonContractOverrideReason}
                      onChange={(e) => set("nonContractOverrideReason", e.target.value)}
                      placeholder="e.g. Agreed with the managing agent while quoting for the contract"
                      className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-2 py-1 text-sm text-ele-body outline-none focus:border-ele-green"
                    />
                    {overrideReasonMissing && (
                      <p role="alert" className="mt-1 text-xs text-ele-strong">
                        Enter a reason for the Core rate. It is the only record of why this visit was not billed at the published non-contract card.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-3 flex flex-wrap gap-2">
                {TIERS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => { tierTouched.current = true; set("tier", t.value); }}
                    className={`rounded-full px-3 py-1 font-menu text-xs tracking-wide transition ${form.tier === t.value ? "bg-ele-green text-ele-bg" : "border border-ele-purple-accent text-ele-body hover:text-ele-strong"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            {/* Session ASM: an agreed price for this one visit. Blank keeps the computed charge below. */}
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="rep-agreed-price" className={labelClass}>Agreed price (ex VAT, optional)</label>
                <input
                  id="rep-agreed-price"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={form.agreedPrice}
                  onChange={(e) => set("agreedPrice", e.target.value)}
                  placeholder="Leave blank for the computed charge"
                  aria-invalid={agreed.state === "invalid" ? true : undefined}
                  className={`${inputClass} ${agreed.state === "invalid" ? "border-ele-danger" : ""}`}
                />
                {agreed.state === "invalid" ? (
                  <p role="alert" className="mt-1 text-xs text-ele-danger">{agreed.message}</p>
                ) : (
                  <p className="mt-1 text-xs text-ele-body opacity-70">
                    {agreed.state === "ok" && agreed.pence === 0
                      ? "Agreed free of charge. A draft invoice for £0.00 is still raised, so the decision is on record in Xero."
                      : "A price agreed for this one visit. It replaces the computed charge on the draft invoice as one line."}
                  </p>
                )}
                {/* Session ASQ (Stream R, R6): the price this call-out already carries is billed unless the office clears it. */}
                {carriedAgreedPence != null && agreed.state !== "invalid" && (
                  <p role="status" className="mt-1 text-xs text-ele-strong">
                    {agreed.state === "blank"
                      ? `The £${(carriedAgreedPence / 100).toFixed(2)} agreed on this call-out's record will be cleared, and the computed charge billed instead.`
                      : agreed.pence === carriedAgreedPence
                        ? "Carried from this call-out's record, so it is billed unless you clear the field."
                        : `This replaces the £${(carriedAgreedPence / 100).toFixed(2)} agreed on this call-out's record.`}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="rep-agreed-reason" className={labelClass}>
                  Reason for the agreed price {agreed.state === "ok" ? "(required)" : "(optional)"}
                </label>
                <input
                  id="rep-agreed-reason"
                  type="text"
                  autoComplete="off"
                  maxLength={500}
                  value={form.agreedPriceReason}
                  onChange={(e) => set("agreedPriceReason", e.target.value)}
                  placeholder="e.g. goodwill after our own fault"
                  aria-invalid={agreedReasonMissing ? true : undefined}
                  className={inputClass}
                />
                {agreedReasonMissing && (
                  <p role="alert" className="mt-1 text-xs text-ele-danger">Enter a reason for the agreed price. It replaces the computed charge, so the call-out records why.</p>
                )}
              </div>
            </div>
            {preview ? (
              <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
                {agreed.state === "ok" && (
                  <p role="status" className="mb-2 text-xs text-ele-strong">
                    The draft invoice will be ONE line at the agreed £{(agreed.pence / 100).toFixed(2)} ex VAT. The computed charge below is shown for reference only.
                  </p>
                )}
                {preview.terminatedForCause ? (
                  <p role="status" className="mb-1 text-xs font-menu uppercase tracking-wide text-[var(--ele-amber-text)]">
                    {preview.terminatedForCause.text}
                  </p>
                ) : null}
                {(preview.banners ?? []).map((b) => (
                  <p key={b} role="status" className="mb-1 text-xs font-menu uppercase tracking-wide text-[var(--ele-amber-text)]">
                    {b}
                  </p>
                ))}
                {preview.lineItems.map((l) => (
                  <div key={l.key} className="flex items-center justify-between gap-2 border-b border-ele-hairline-soft py-1">
                    <input
                      value={l.key === "firstHour" ? form.descFirstHour : l.key === "additional" ? form.descAdditional : form.descTravel}
                      onChange={(e) => set(l.key === "firstHour" ? "descFirstHour" : l.key === "additional" ? "descAdditional" : "descTravel", e.target.value)}
                      placeholder={l.description}
                      className="flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-ele-body outline-none focus:border-ele-purple-accent"
                    />
                    <span className="whitespace-nowrap text-ele-body">{l.rate} x {l.qty}</span>
                    <span className="w-20 whitespace-nowrap text-right font-heading text-ele-strong">{l.amount}</span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between text-ele-body"><span>Subtotal (ex VAT)</span><span>{preview.subtotal}</span></div>
                <div className="flex justify-between text-ele-body"><span>VAT at 20%</span><span>{preview.vat}</span></div>
                <div className="mt-1 flex justify-between border-t border-ele-green pt-1 font-heading text-ele-green"><span>Total (inc VAT)</span><span>{preview.total}</span></div>
                <p className="mt-2 text-xs text-ele-body opacity-70">{preview.chargeNote}</p>
                {preview.chargeChange && (
                  <p className="mt-2 rounded-ele bg-ele-amber-wash p-2 text-xs text-ele-amber-wash-ink">
                    {`This call-out already has a recorded charge of ${gbpExact(preview.chargeChange.storedTotalPence / 100)} ex VAT. Generating will replace it with ${gbpExact(preview.chargeChange.newTotalPence / 100)} ex VAT, and you will be asked to confirm.`}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-ele-body">Calculating charge...</p>
            )}
          </>
        )}
        {/* Session AHJ: charges hidden means the invoice was raised in Xero by hand, so the report
            has no draft to take its reference from and would otherwise carry the internal
            "CO-<id>". The office cites their own Xero number instead. Shown ONLY in this branch:
            with charges on, the server ignores the field, and offering it there would invite the
            office to type a number that is silently discarded. */}
        {!form.showCharges && (
          <div className="max-w-sm">
            <label htmlFor="rep-invoice-reference" className={labelClass}>
              Xero invoice number (optional)
            </label>
            <input
              id="rep-invoice-reference"
              value={form.invoiceReference}
              onChange={(e) => set("invoiceReference", e.target.value)}
              placeholder="INV-2431"
              aria-invalid={invoiceRefError ? true : undefined}
              className={`${inputClass} ${invoiceRefError ? "border-ele-danger" : ""}`}
            />
            {invoiceRefError ? (
              <p role="alert" className="mt-1 text-xs text-ele-danger">{invoiceRefError}</p>
            ) : (
              <p className="mt-1 text-xs text-ele-body opacity-70">
                {keptReference
                  ? `Shown as the reference on the report. Leave it blank to keep ${keptReference}.`
                  : "Shown as the reference on the report. Leave it blank to use the platform reference."}
              </p>
            )}
          </div>
        )}
      </section>

      <Field label="Client signatory name (optional)" value={form.clientSignatoryName} onChange={(v) => set("clientSignatoryName", v)} />

      {/* Result / action bar */}
      {result && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <StatusPill word={`Report ${result.reportRef}`} tone="green" />
          {result.xero.status === "created" && result.xero.invoiceNumber && <StatusPill word={`Xero draft ${result.xero.invoiceNumber} created`} tone="green" />}
          {result.xero.status === "existing_kept" && <StatusPill word="An existing Xero invoice covers this call-out; reconcile it there" tone="amber" />}
          {result.xero.status === "no_number" && <StatusPill word="Xero draft created (number pending in Xero)" tone="blue" />}
          {(result.xero.status === "not_connected" || result.xero.status === "error") && <StatusPill word="Xero invoice not created, create it by hand" tone="amber" />}
          {/* Session AHJ: hiding charges skips the void-and-re-raise, so a DRAFT the platform
              raised at intake for this same attendance is still live in Xero. Nothing was voided
              here; the office decides which invoice stands. */}
          {result.unvoidedDraft && (
            <StatusPill
              tone="amber"
              word={
                typeof result.unvoidedDraft === "string"
                  ? `This call-out still has a platform DRAFT in Xero (${result.unvoidedDraft}). Void it there if you have invoiced by hand.`
                  : "This call-out still has a platform DRAFT in Xero. Void it there if you have invoiced by hand."
              }
            />
          )}
          {result.reportUrl && (
            <a href={result.reportUrl} target="_blank" rel="noopener noreferrer" className="hover:underline"><StatusPill word="Open in Drive" tone="purple" /></a>
          )}
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-ele-danger">{error}</p>}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        {chargeChangeRefusal && (
          <div role="alert" className="mb-3 w-full rounded-ele bg-ele-amber-wash p-3 text-sm text-ele-amber-wash-ink">
            <p>{chargeChangeRefusal}</p>
            <div className="mt-2 flex flex-wrap gap-3">
              <ConfirmButton
                onConfirm={() => void generate(true)}
                label="Replace the recorded charge and generate"
                confirmLabel="Press again to replace it"
                busy={generating}
                busyLabel="Generating..."
              />
              <button type="button" onClick={() => setChargeChangeRefusal(null)} className="font-menu text-xs tracking-wide text-ele-body hover:underline">
                Keep the recorded charge
              </button>
            </div>
          </div>
        )}
        <Button onClick={() => void generate()} disabled={busy || !canGenerate || chargeChangeRefusal != null} className={MODAL_ACTION_BTN}>
          {generating ? "Generating..." : result ? "Regenerate PDF" : "Generate PDF"}
        </Button>
        <label className="flex items-center gap-2 text-sm text-ele-body">
          <input type="checkbox" checked={saveToDrive} onChange={(e) => setSaveToDrive(e.target.checked)} />
          Save to Drive
        </label>
        <button type="button" onClick={onClose} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2.5 text-sm text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}>Close</button>
      </div>
    </ModalShell>
  );
}

function fieldId(label: string): string {
  return `rep-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
}

function Field({ label, value, onChange, type = "text", className = "" }: { label: string; value: string; onChange: (v: string) => void; type?: string; className?: string }) {
  const id = fieldId(label);
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
    </div>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const id = fieldId(label);
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <input id={id} type="number" min={0} step="0.5" value={value} onFocus={(e) => e.target.select()} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} className={inputClass} />
    </div>
  );
}

function TextArea({ label, value, onChange, className = "" }: { label: string; value: string; onChange: (v: string) => void; className?: string }) {
  const id = fieldId(label);
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <textarea id={id} rows={2} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
    </div>
  );
}
